import React, { useRef, useState, useCallback, useEffect } from 'react';
import Typography from '@mui/material/Typography';
import { Box, Button, alpha, CircularProgress, useTheme, useMediaQuery } from '@mui/material';
import Webcam from 'react-webcam';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import { Add, HourglassEmpty } from '@mui/icons-material';
import { PointsIcon } from 'components/common/PointsIcon';
import theme from 'theme';
import { Link } from 'react-router-dom';
import { paths } from 'routing/paths';
import { CreateDepositDto, Deposit, Item, Material, Session } from 'types/api.types';
import { getItems, getMaterials } from 'services/lookupsService';
import {
  createDeposit,
  deleteDeposit,
  endSession,
  getCurrentDeposits,
  getCurrentSession,
} from 'services/depositService';
import { getImageUrl } from 'services/imageService';
import DepositCard from 'components/common/DepositCard';

enum ScreenType {
  Loading,
  Capture,
  RecycleBin,
  Success,
}

const videoConstraints = {
  width: 720,
  height: 720,
  facingMode: 'environment',
};

const Recycle: React.FC = () => {
  const webcamRef = useRef<Webcam>(null);
  const [imgSrc, setImgSrc] = useState<string | null>(null);
  const [isCameraReady, setIsCameraReady] = useState<boolean>(false);
  const [openBottomSheet, setOpenBottomSheet] = useState<boolean>(false);
  const [selectedMaterial, setSelectedMaterial] = useState<string>('');
  const [selectedItem, setSelectedItem] = useState<string>('');
  const [recycleBin, setRecycleBin] = useState<Deposit[]>([]);
  const [currentScreen, setCurrentScreen] = useState<ScreenType>(ScreenType.Loading);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isDepositing, setIsDepositing] = useState<boolean>(false);
  const [isCapturing, setIsCapturing] = useState<boolean>(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [materials, setMaterials] = useState<Material[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [sessionId, setSessionId] = useState<string>('');

  useEffect(() => {
    getMaterials().then((items) => items && setMaterials(items));
    getItems().then((items) => items && setItems(items));
    getCurrentSession().then((session: Session | null) => {
      setSessionId(session?.id || '');
      getCurrentDeposits().then((deposits) => {
        setRecycleBin(deposits);
        setCurrentScreen(deposits?.length ? ScreenType.RecycleBin : ScreenType.Capture);
      });
    });
  }, []);

  const capture = useCallback(() => {
    setIsCapturing(true);
    const imageSrc = webcamRef.current?.getScreenshot();
    if (imageSrc) {
      setImgSrc(imageSrc);
    }
    setTimeout(() => {
      setIsCapturing(false);
      setOpenBottomSheet(true);
    }, 1000);
  }, [webcamRef, setImgSrc, setOpenBottomSheet]);

  const closeBottomSheet = () => {
    setOpenBottomSheet(false);
    setSelectedMaterial('');
    setSelectedItem('');
  };

  const handleMaterialChange = (event: SelectChangeEvent<string>) => {
    setSelectedMaterial(event.target.value);
    setSelectedItem('');
  };

  const handleItemChange = (event: SelectChangeEvent<string>) => {
    setSelectedItem(event.target.value);
  };

  const handleAddToBin = async () => {
    if (selectedMaterial && selectedItem && imgSrc) {
      const newItem: CreateDepositDto = {
        material: +selectedMaterial,
        item: +selectedItem,
      };

      setIsDepositing(true);
      try {
        const base64Response = await fetch(imgSrc);
        const blob = await base64Response.blob();
        const file = new File([blob], `recycle_item_${Date.now()}.jpeg`, {
          type: 'image/jpeg',
        });
        const deposit = await createDeposit(newItem, file);
        if (deposit) {
          setRecycleBin((prevBin) => [...prevBin, deposit]);
        }
        const session = await getCurrentSession();
        if (session) {
          setSessionId(session.id);
        }
        setIsDepositing(false);
        setImgSrc(null);
        closeBottomSheet();
        setCurrentScreen(ScreenType.RecycleBin);
      } catch (e) {
        console.log(e);
      } finally {
        setIsDepositing(false);
      }
    }
  };

  const handleRetake = () => {
    setImgSrc(null);
    setIsCameraReady(false);
    closeBottomSheet();
    setCurrentScreen(ScreenType.Capture);
  };

  const onUserMedia = useCallback(() => {
    setIsCameraReady(true);
  }, []);

  const handleAddMore = () => {
    setCurrentScreen(ScreenType.Capture);
    setImgSrc(null);
    setIsCameraReady(false);
    if (currentScreen === ScreenType.Success) {
      setRecycleBin([]);
    }
  };

  const handleRemoveItem = async (id: number) => {
    await deleteDeposit(id);
    if (recycleBin.length === 1) {
      handleRetake();
    }

    setRecycleBin((prevBin) => prevBin.filter((item) => item.id !== id));
  };

  const handleSubmit = async () => {
    if (!sessionId || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    await endSession(sessionId);
    setIsSubmitting(false);
    setCurrentScreen(ScreenType.Success);
  };

  const renderImagePlacementOverlay = () => (
    <Box
      sx={{
        position: 'absolute',
        width: '230px',
        height: '280px',
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%)',
        pointerEvents: 'none',
        zIndex: 1,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '40px',
          height: '40px',
          borderTop: '2px solid white',
          borderLeft: '2px solid white',
          borderTopLeftRadius: '16px',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '40px',
          height: '40px',
          borderTop: '2px solid white',
          borderRight: '2px solid white',
          borderTopRightRadius: '16px',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: '40px',
          height: '40px',
          borderBottom: '2px solid white',
          borderLeft: '2px solid white',
          borderBottomLeftRadius: '16px',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          right: 0,
          width: '40px',
          height: '40px',
          borderBottom: '2px solid white',
          borderRight: '2px solid white',
          borderBottomRightRadius: '16px',
        }}
      />
    </Box>
  );

  const totalPendingPoints = recycleBin.map((item) => item.points || 0).reduce((a, b) => a + b, 0);

  if (currentScreen === ScreenType.Loading) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      {currentScreen !== ScreenType.Success && (
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mt: 4 }}>
          Snap, Recycle, Earn!
        </Typography>
      )}

      {currentScreen === ScreenType.Capture ? (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            maxWidth: { sm: '100%', md: 400 },
            mt: 4,
            pb: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 3,
            position: 'relative',
          }}
        >
          {imgSrc ? (
            <Box sx={{ position: 'relative' }}>
              <img src={imgSrc} alt="Captured" style={{ width: '100%', height: 'auto' }} />
              {renderImagePlacementOverlay()}
            </Box>
          ) : (
            <Box sx={{ position: 'relative' }}>
              <Webcam
                audio={false}
                ref={webcamRef}
                screenshotFormat="image/jpeg"
                width={videoConstraints.width}
                height={videoConstraints.height}
                videoConstraints={videoConstraints}
                onUserMedia={onUserMedia}
                style={{ width: '100%', height: 'auto', borderRadius: '8px' }}
              />
              {renderImagePlacementOverlay()}
            </Box>
          )}
          {!openBottomSheet && (
            <Box sx={{ width: '100%' }}>
              <Box sx={{ width: '100%' }}>
                <Button
                  variant="contained"
                  color="primary"
                  sx={{ width: '100%', px: 2 }}
                  onClick={capture}
                  disabled={!isCameraReady || isCapturing}
                >
                  {isCapturing ? <CircularProgress size={24} color="inherit" /> : 'Capture'}
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      ) : (
        <>
          {currentScreen === ScreenType.RecycleBin ? (
            <Box
              sx={{
                width: '100%',
                maxWidth: 400,
                mt: 4,
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              <Typography variant="h6">Recycle Bin</Typography>
              {recycleBin.map((item) => (
                <DepositCard
                  items={items}
                  materials={materials}
                  item={item}
                  key={item.id}
                  onDeleteClick={() => handleRemoveItem(item.id)}
                />
              ))}
              <Button variant="outlined" color="primary" onClick={handleAddMore} sx={{ mb: 1 }}>
                <Add /> Add More
              </Button>
              <Box
                sx={(theme) => ({
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flexDirection: 'column',
                  background: alpha(theme.palette.primary.main, 0.1),
                  border: `1px solid ${theme.palette.primary.main}`,
                  p: 1,
                  mb: 1,
                  borderRadius: 1,
                })}
              >
                <Typography sx={(theme) => ({ color: theme.palette.grey[600] })}>
                  Total Points
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PointsIcon size={32} />{' '}
                  <Typography sx={{ fontWeight: 600, fontSize: 18 }}>
                    {totalPendingPoints}
                  </Typography>
                </Box>
              </Box>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Submit'}
              </Button>
            </Box>
          ) : (
            <Box
              sx={{
                width: '100%',
                maxWidth: 400,
                mt: 4,
                p: 4,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                gap: 2,
              }}
            >
              <Typography
                variant="h5"
                gutterBottom
                sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}
              >
                Recycle bin submitted
                <HourglassEmpty
                  sx={(theme) => ({ color: theme.palette.primary.main, fontSize: 32 })}
                />
              </Typography>
              <Typography variant="body1" sx={{ color: theme.palette.grey[600] }}>
                We'll review your items soon and approve your points!
              </Typography>
              <Box
                sx={(theme) => ({
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flexDirection: 'column',
                  background: alpha(theme.palette.primary.main, 0.1),
                  border: `1px solid ${theme.palette.primary.main}`,
                  p: 1,
                  px: 3,
                  mb: 1,
                  borderRadius: 1,
                  mt: 2,
                })}
              >
                <Typography sx={(theme) => ({ color: theme.palette.grey[600] })}>
                  Total pending points
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PointsIcon size={32} />{' '}
                  <Typography sx={{ fontWeight: 600, fontSize: 18 }}>
                    {totalPendingPoints}
                  </Typography>
                </Box>
              </Box>
              <Button variant="contained" color="primary" onClick={handleAddMore} sx={{ mt: 2 }}>
                Recycle more items
              </Button>
              <Link to={paths.rewards} style={{ textDecoration: 'none', color: 'inherit' }}>
                <Button variant="text" sx={{ textTransform: 'none' }}>
                  My rewards
                </Button>
              </Link>
            </Box>
          )}
        </>
      )}

      <Dialog
        open={openBottomSheet}
        onClose={handleRetake}
        slotProps={{
          paper: {
            sx: {
              maxWidth: isMobile ? '100%' : 400,
              width: '100%',
              position: 'absolute',
              bottom: 0,
              margin: 0,
              borderRadius: '8px 8px 0 0',
              boxShadow: 'none',
            },
          },
          backdrop: {
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.1)',
            },
          },
        }}
        keepMounted
      >
        <DialogTitle>Let's identify your item!</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mb: 2, mt: 2 }}>
            <InputLabel id="material-select-label">Material</InputLabel>
            <Select
              labelId="material-select-label"
              value={selectedMaterial}
              label="Material"
              onChange={handleMaterialChange}
            >
              {materials.map((material) => (
                <MenuItem key={material.id} value={material.id}>
                  {material.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth disabled={!selectedMaterial}>
            <InputLabel id="item-select-label">Item</InputLabel>
            <Select
              labelId="item-select-label"
              value={selectedItem}
              label="Item"
              onChange={handleItemChange}
            >
              {selectedMaterial &&
                items
                  .filter((item) => item.material === +selectedMaterial)
                  .map((item) => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.name}
                    </MenuItem>
                  ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ display: 'flex', justifyContent: 'space-between', p: 3, pt: 1 }}>
          <Button onClick={handleRetake}>Retry</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleAddToBin}
            disabled={!selectedMaterial || !selectedItem || isDepositing}
          >
            {isDepositing ? <CircularProgress size={24} color="inherit" /> : 'Add to Recycle Bin'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Recycle;
